import React, { forwardRef } from 'react';
import { useParams } from 'react-router-dom';
import { CreateRequestModalProps, CreateRequestModalRef } from './CreateRequestModal/CreateRequestModal';
import { AskRequest, AskRequestStatus, CreateAskRequest } from '@hone-automation/common';
import { useQueueContext } from '../Queue/QueueContext';
import { useEvent } from '../../../hooks/useEvent';
import { useCreateQueueRequestMutation } from 'hooks/queue/useCreateQueueRequestMutation';

export type WithQueueProps = {
  isOpen: boolean;
  handleClose: () => void;
};

const withQueue = <P extends WithQueueProps>(
  WrappedComponent: React.ForwardRefExoticComponent<
    React.PropsWithoutRef<CreateRequestModalProps> & React.RefAttributes<CreateRequestModalRef>
  >
) => {
  return forwardRef<CreateRequestModalRef, P>(function WithQueue(props, ref) {
    const createRequestModalRef = React.useRef<CreateRequestModalRef>(null);
    const { locationId } = useParams();
    const { dispatch } = useQueueContext();
    const { isOpen, handleClose } = props;
    const { dispatch: emitEvent } = useEvent('onQueueRequestChangeStatus');

    const { mutate } = useCreateQueueRequestMutation(locationId!, data => {
      dispatch({
        type: 'ADD_NEW_REQUEST',
        payload: {
          request: data as unknown as AskRequest,
          status: data.status,
        },
      });
      emitEvent({ field: data.status, duration: 5000 });
      handleClose();
    });

    const createRequest = async (data: CreateAskRequest) => {
      mutate(data);
      createRequestModalRef.current?.resetState();
    };

    const wrappedComponentProps = {
      isOpen,
      onSubmit: createRequest,
      handleClose,
      resetState: handleClose,
      ref: createRequestModalRef,
      locationId,
      title: 'New Queue Request',
    } as CreateRequestModalProps;

    return <WrappedComponent {...wrappedComponentProps} />;
  });
};

export default withQueue;
